import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { supportQueriesApi } from "../../services/api";
import { PageHeader } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  ChevronDown,
  Check,
  X,
  Eye,
  Pencil,
  Trash2,
  MessageSquare,
  Image as ImageIcon,
  ExternalLink,
  AlertCircle,
  Search,
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: "",            label: "All Statuses" },
  { value: "OPEN",        label: "Open"         },
  { value: "IN_PROGRESS", label: "In Progress"  },
  { value: "RESOLVED",    label: "Resolved"     },
  { value: "CLOSED",      label: "Closed"       },
];

const STATUS_META = {
  OPEN:        { bg: "rgba(239,68,68,0.10)",   color: "#dc2626", border: "rgba(239,68,68,0.25)"   },
  IN_PROGRESS: { bg: "rgba(37,99,235,0.10)",   color: "#2563eb", border: "rgba(37,99,235,0.25)"   },
  RESOLVED:    { bg: "rgba(16,185,129,0.10)",  color: "#059669", border: "rgba(16,185,129,0.25)"  },
  CLOSED:      { bg: "rgba(107,114,128,0.10)", color: "#6b7280", border: "rgba(107,114,128,0.25)" },
};

function fmt(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Column-header filter dropdown ─────────────────────────────────────────────
function HeaderDropdown({ value, onChange, options }) {
  const [open, setOpen]     = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const ref    = useRef(null);
  const btnRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect              = btnRef.current.getBoundingClientRect();
    const menuWidth         = 180;
    const estimatedMenuHeight = 260;
    const gap               = 8;
    let left = rect.left;
    let top  = rect.bottom + gap;
    if (left + menuWidth > window.innerWidth - 12) left = window.innerWidth - menuWidth - 12;
    if (top + estimatedMenuHeight > window.innerHeight - 12) top = Math.max(12, window.innerHeight - estimatedMenuHeight - 12);
    setMenuPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize",  updatePosition);
    window.addEventListener("scroll",  updatePosition, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize",  updatePosition);
      window.removeEventListener("scroll",  updatePosition, true);
    };
  }, [open, updatePosition]);

  const menu = open
    ? ReactDOM.createPortal(
        <div ref={ref} className="header-dropdown-scroll" style={{ position: "fixed", top: menuPos.top, left: menuPos.left, zIndex: 99999, minWidth: 180, maxHeight: 260, background: "#0F3D3E", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.35)", overflowX: "hidden", overflowY: "scroll" }}>
          {options.map((opt) => {
            const isActive = opt.value === value;
            return (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.10)"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                style={{ padding: "9px 14px", cursor: "pointer", fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? "#fff" : "rgba(255,255,255,0.75)", background: isActive ? "rgba(255,255,255,0.12)" : "transparent", display: "flex", alignItems: "center", gap: 8, transition: "background 0.15s", whiteSpace: "nowrap" }}
              >
                {opt.label}
                {isActive && <Check size={12} strokeWidth={3} color="#fff" style={{ marginLeft: "auto" }} />}
              </div>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!open) updatePosition(); setOpen((o) => !o); }}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 4, cursor: "pointer", padding: 0, border: value ? "1px solid rgba(15,61,62,0.6)" : "1px solid var(--border)", background: value ? "rgba(15,61,62,0.12)" : "transparent", color: value ? "#0F3D3E" : "var(--text-muted)", transition: "all 0.18s" }}
      >
        <ChevronDown size={11} strokeWidth={2.8} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.18s" }} />
      </button>
      {menu}
    </>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { bg: "rgba(107,114,128,0.1)", color: "#6b7280", border: "rgba(107,114,128,0.2)" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, whiteSpace: "nowrap" }}>
      {status || "—"}
    </span>
  );
}

// ── Inline Status Changer ─────────────────────────────────────────────────────
function StatusChanger({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = 180;
    const estimatedMenuHeight = 220;
    const gap = 8;

    let left = rect.left;
    let top = rect.bottom + gap;

    if (left + menuWidth > window.innerWidth - 12) {
      left = window.innerWidth - menuWidth - 12;
    }

    if (top + estimatedMenuHeight > window.innerHeight - 12) {
      top = Math.max(12, rect.top - estimatedMenuHeight - gap);
    }

    setMenuPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return;

    updatePosition();

    const handleClickOutside = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  const change = async (newStatus) => {
    if (newStatus === row.status) {
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      await supportQueriesApi.updateStatus(row.id, newStatus);
      showToast(`Status updated to ${newStatus}`, "success");
      onChanged(row.id, newStatus);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update status", "error");
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={ref}
          className="header-dropdown-scroll"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 99999,
            minWidth: 180,
            background: "#0F3D3E",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}
        >
          {STATUS_OPTIONS.filter((s) => s.value).map((opt) => {
            const isActive = opt.value === row.status;
            return (
              <div
                key={opt.value}
                onClick={() => change(opt.value)}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isActive
                    ? "rgba(255,255,255,0.12)"
                    : "transparent";
                }}
                style={{
                  padding: "9px 14px",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                  background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "background 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
                {isActive && (
                  <Check
                    size={11}
                    strokeWidth={3}
                    color="#fff"
                    style={{ marginLeft: "auto" }}
                  />
                )}
              </div>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!open) updatePosition();
          setOpen((o) => !o);
        }}
        disabled={loading}
        title="Change Status"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          border: "1px solid var(--border)",
          background: "transparent",
          color: "var(--text-secondary)",
          cursor: loading ? "not-allowed" : "pointer",
          transition: "all 0.15s",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#0F3D3E";
          e.currentTarget.style.color = "#0F3D3E";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.color = "var(--text-secondary)";
        }}
      >
        {loading ? "…" : (
          <>
            <Pencil size={11} strokeWidth={2.5} />
            Status
            <ChevronDown
              size={9}
              strokeWidth={3}
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s",
              }}
            />
          </>
        )}
      </button>

      {menu}
    </>
  );
}

// ── Detail Slide-Over Drawer ──────────────────────────────────────────────────
function DetailDrawer({ entry, onClose, onStatusChanged }) {
  if (!entry) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: 480, background: "var(--bg-card)", borderLeft: "1px solid var(--border)", boxShadow: "-12px 0 48px rgba(0,0,0,0.18)", zIndex: 1001, display: "flex", flexDirection: "column", overflowY: "auto" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(15,61,62,0.04)" }}>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text-primary)", marginBottom: 2 }}>Query Detail</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>#{entry.id}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--border)", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* User info */}
          <div style={{ background: "var(--bg-input)", borderRadius: "var(--radius)", padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>User</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", marginBottom: 3 }}>{entry.userName || "—"}</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5 }}>
              <Search size={11} strokeWidth={2} /> {entry.userMobile || "—"}
            </div>
            <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)", marginTop: 4 }}>ID: {entry.userId}</div>
          </div>

          {/* Status + dates */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "var(--bg-input)", borderRadius: "var(--radius)", padding: "12px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Status</div>
              <StatusBadge status={entry.status} />
              <div style={{ marginTop: 10 }}>
                <StatusChanger row={entry} onChanged={(id, newStatus) => onStatusChanged(id, newStatus)} />
              </div>
            </div>
            <div style={{ background: "var(--bg-input)", borderRadius: "var(--radius)", padding: "12px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Timestamps</div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 4 }}>
                <span style={{ color: "var(--text-muted)" }}>Created: </span>{fmt(entry.createdAt)}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--text-muted)" }}>Updated: </span>{fmt(entry.updatedAt)}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Description</div>
            <div style={{ background: "var(--bg-input)", borderRadius: "var(--radius)", padding: "14px 16px", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7, border: "1px solid var(--border)" }}>
              {entry.description || "—"}
            </div>
          </div>

          {/* Screenshot */}
          {entry.screenshotUrl && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <ImageIcon size={12} strokeWidth={2.5} /> Screenshot
              </div>
              <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden", background: "var(--bg-input)", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80 }}>
                <img
                  src={entry.screenshotUrl}
                  alt="User screenshot"
                  style={{ width: "100%", maxHeight: 300, objectFit: "contain", display: "block", cursor: "pointer" }}
                  onClick={() => window.open(entry.screenshotUrl, "_blank", "noopener,noreferrer")}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.innerHTML =
                      `<div style="padding:20px;text-align:center;font-size:12px;color:var(--text-muted)">Screenshot unavailable</div>`;
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 5, display: "flex", alignItems: "center", gap: 4 }}>
                <ExternalLink size={11} strokeWidth={2} /> Click image to view full size
              </div>
            </div>
          )}

          {/* Active flag */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: entry.isActive ? "#059669" : "#dc2626" }} />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {entry.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
function ConfirmDeleteModal({ entry, onClose, onConfirm, loading }) {
  if (!entry) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "var(--bg-card)", borderRadius: "var(--radius-lg)", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", zIndex: 1101, padding: "28px 32px", width: 420, maxWidth: "90vw" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <Trash2 size={28} strokeWidth={1.5} color="#dc2626" />
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", marginBottom: 8 }}>
          Delete Support Query?
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
          This will permanently delete the query from <strong style={{ color: "var(--text-primary)" }}>{entry.userName || entry.userId}</strong>. This cannot be undone.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.65 : 1 }}>
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SupportQueries() {
  const [data,     setData]     = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [search,   setSearch]   = useState("");
  const [status,   setStatus]   = useState("");
  const [page,     setPage]     = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (pg, st) => {
    setLoading(true);
    try {
      const r = await supportQueriesApi.getAll(pg, 20, "", st);
      setData(r.data.data);
    } catch {
      setData({ content: [], totalPages: 0, totalElements: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, status); }, [page, status, load]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); load(0, status); };
  const handleClear  = () => { setSearch(""); setStatus(""); setPage(0); load(0, ""); };

  const handleStatusChanged = (id, newStatus) => {
    setData((prev) => ({ ...prev, content: prev.content.map((r) => r.id === id ? { ...r, status: newStatus } : r) }));
    setSelected((prev) => prev && prev.id === id ? { ...prev, status: newStatus } : prev);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await supportQueriesApi.delete(toDelete.id);
      showToast("Query deleted", "success");
      setData((prev) => ({ ...prev, content: prev.content.filter((r) => r.id !== toDelete.id), totalElements: prev.totalElements - 1 }));
      setToDelete(null);
      if (selected?.id === toDelete.id) setSelected(null);
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to delete", "error");
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = search || status;

  const getPages = () => {
    const totalPages = data.totalPages;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const pages = [];
    if (page <= 3)                    pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
    else if (page >= totalPages - 4)  pages.push(0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
    else                              pages.push(0, "...", page - 1, page, page + 1, "...", totalPages - 1);
    return pages;
  };

  const btnStyle = (isActive, isDisabled) => ({
    minWidth: 34, height: 34, padding: "0 10px",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: isActive ? "none" : "1px solid var(--border)",
    borderRadius: 8,
    background: isActive ? "#0F3D3E" : "transparent",
    color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
    fontSize: 13, fontWeight: isActive ? 700 : 500,
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.45 : 1,
    transition: "all 0.15s",
    fontFamily: "var(--font-body)",
  });

  const TH = ({ children, style = {} }) => (
    <th style={{ padding: "11px 16px", textAlign: "left", whiteSpace: "nowrap", fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", ...style }}>
      {children}
    </th>
  );

  const filteredContent = search.trim()
    ? (data.content || []).filter((row) => (row.userName || "").toLowerCase().includes(search.trim().toLowerCase()))
    : (data.content || []);

  return (
    <div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .header-dropdown-scroll { scrollbar-gutter: stable; }
        .header-dropdown-scroll::-webkit-scrollbar { width: 10px; }
        .header-dropdown-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.06); border-radius: 10px; }
        .header-dropdown-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.35); border-radius: 10px; border: 2px solid #0F3D3E; }
        .header-dropdown-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.5); }
      `}</style>

      <PageHeader title="Support Queries" subtitle="Browse and manage all user-submitted support queries" />

      {/* ── Search bar ── */}
      <form onSubmit={handleSearch} style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <div style={{ position: "relative" }}>
          <Search size={14} strokeWidth={2} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
          <input
            placeholder="Search by username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240, padding: "9px 14px 9px 32px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", outline: "none" }}
          />
        </div>
        <button type="submit" style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "none", background: "#0F3D3E", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)" }}>
          Search
        </button>
        <button type="button" onClick={handleClear} style={{ padding: "9px 16px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>
          Clear
        </button>
      </form>

      {/* ── Table card ── */}
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", overflow: "visible" }}>

        {/* Table header bar */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, background: "rgba(15,61,62,0.03)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 4, height: 18, borderRadius: 2, background: "#0F3D3E", flexShrink: 0 }} />
            <span style={{ fontFamily: "var(--font-display)", fontSize: 14, color: "#0F3D3E", fontWeight: 600 }}>Support Queries</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--border)", padding: "2px 8px", borderRadius: 20 }}>
              {data.totalElements} total
            </span>
          </div>

          {hasFilters && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {search && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Search size={10} strokeWidth={2.5} /> {search}
                </span>
              )}
              {status && (
                <span style={{ fontSize: 11, background: STATUS_META[status]?.bg, color: STATUS_META[status]?.color, borderRadius: 20, padding: "2px 10px", fontWeight: 600, border: `1px solid ${STATUS_META[status]?.border}` }}>
                  {status}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
            <div style={{ width: 32, height: 32, margin: "0 auto 10px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            Loading…
          </div>
        ) : (
          <div style={{ overflowX: "auto", overflowY: "visible", borderBottomLeftRadius: "var(--radius-lg)", borderBottomRightRadius: "var(--radius-lg)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-input)", borderBottom: "1px solid var(--border)" }}>
                  <TH>#</TH>
                  <TH>User</TH>
                  <TH>Description</TH>
                  <TH>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, position: "relative" }}>
                      <span>Status</span>
                      <HeaderDropdown value={status} onChange={(v) => { setStatus(v); setPage(0); }} options={STATUS_OPTIONS} />
                    </div>
                  </TH>
                  <TH>Created At</TH>
                  <TH>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {filteredContent.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "48px 20px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                        <MessageSquare size={32} strokeWidth={1.5} color="var(--text-muted)" />
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>No support queries found</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {hasFilters ? "Try clearing filters above." : "No queries have been submitted yet."}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : filteredContent.map((row, idx) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }} onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-input)"} onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>{page * 20 + idx + 1}</span>
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{row.userName || "—"}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                        <Search size={10} strokeWidth={2} /> {row.userMobile || "—"}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", maxWidth: 320 }}>
                      <span title={row.description} style={{ fontSize: 12, color: "var(--text-secondary)", cursor: "pointer" }} onClick={() => setSelected(row)}>
                        {row.description?.length > 90 ? row.description.slice(0, 90) + "…" : row.description || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <StatusBadge status={row.status} />
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{fmt(row.createdAt)}</span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                        {/* View */}
                        <button
                          onClick={() => setSelected(row)}
                          title="View Detail"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", cursor: "pointer", transition: "all 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = "#0F3D3E"; e.currentTarget.style.borderColor = "#0F3D3E"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                        >
                          <Eye size={14} strokeWidth={2} />
                        </button>

                        <StatusChanger row={row} onChanged={handleStatusChanged} />

                        {/* Delete */}
                        <button
                          onClick={() => setToDelete(row)}
                          title="Delete"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 6, border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.08)", color: "#dc2626", cursor: "pointer", transition: "all 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.18)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
                        >
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Showing <strong style={{ color: "var(--text-primary)" }}>{filteredContent.length}</strong> of{" "}
            <strong style={{ color: "var(--text-primary)" }}>{search.trim() ? filteredContent.length : data.totalElements}</strong> queries
          </span>

          {data.totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={btnStyle(false, page === 0)}>← Prev</button>
              {getPages().map((p, i) =>
                p === "..." ? (
                  <span key={`d${i}`} style={{ padding: "0 4px", color: "var(--text-muted)", fontSize: 14, userSelect: "none" }}>…</span>
                ) : (
                  <button key={p} onClick={() => setPage(p)} style={btnStyle(page === p, false)} onMouseEnter={(e) => { if (page !== p) e.currentTarget.style.background = "rgba(15,61,62,0.08)"; }} onMouseLeave={(e) => { if (page !== p) e.currentTarget.style.background = "transparent"; }}>
                    {p + 1}
                  </button>
                )
              )}
              <button disabled={page >= data.totalPages - 1} onClick={() => setPage((p) => p + 1)} style={btnStyle(false, page >= data.totalPages - 1)}>Next →</button>
            </div>
          )}
        </div>
      </div>

      <DetailDrawer entry={selected} onClose={() => setSelected(null)} onStatusChanged={handleStatusChanged} />
      <ConfirmDeleteModal entry={toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete} loading={deleting} />
    </div>
  );
}